import { useEffect, useMemo, useState } from "react";
import { EyeOff, RotateCcw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import CostButton from "@/components/ai/CostButton";
import ReviewImage from "./ReviewImage";
import { SCORE_LABELS, SEVERITY, VIEWPORT, targetTitle } from "./reviewText";

// defects of every analysed page grouped by the element to correct (a shared header found on 3 pages is one group)
export function groupDefects(rev) {
  const groups = {};
  Object.values(rev.analysis?.pages || {}).forEach((pg) => pg.defects.forEach((d) => {
    const g = groups[d.target_id] || (groups[d.target_id] = { id: d.target_id, kind: d.target_kind, name: d.target_name, page_id: d.page_id, page_name: d.page_name, defects: [] });
    g.defects.push(d);
  }));
  const rank = (g) => Math.min(...g.defects.map((d) => ({ high: 0, medium: 1, low: 2 }[d.severity])));
  return Object.values(groups).sort((a, b) => rank(a) - rank(b));
}

function cropOf(rev, g) {
  const pages = rev.render?.pages || [];
  const pg = pages.find((p) => p.page_id === g.page_id) || pages[0];
  return pg?.views?.desktop?.crops?.[g.id];
}

function Scores({ pages }) {
  return (
    <div className="grid gap-2 md:grid-cols-2" data-testid="review-page-scores">
      {pages.map((p) => (
        <div key={p.page_id} className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-sm font-semibold text-slate-900">{p.name}</p>
          <p className="mt-0.5 text-xs text-slate-600">{p.summary}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(p.scores || {}).map(([k, v]) => (
              <span key={k} className={`rounded-full px-2 py-0.5 text-[11px] tabular-nums ${v >= 8 ? "bg-emerald-50 text-emerald-700" : v >= 6 ? "bg-slate-100 text-slate-700" : "bg-amber-50 text-amber-800"}`}>{SCORE_LABELS[k]} {v}/10</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DefectRow({ d, on, toggle, hook }) {
  const sev = SEVERITY[d.severity];
  return (
    <li className={`flex gap-2.5 py-2 ${d.dismissed ? "opacity-50" : ""}`} data-testid={`review-defect-${d.id}`}>
      <Checkbox checked={on} disabled={d.dismissed} onCheckedChange={toggle} className="mt-0.5" data-testid={`review-defect-check-${d.id}`} />
      <div className="min-w-0 flex-1 text-xs">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`rounded border px-1.5 py-px text-[10px] font-medium ${sev.cls}`}>{sev.label}</span>
          <span className="font-medium text-slate-900">{d.category_label}</span>
          <span className="text-slate-500">· {VIEWPORT[d.viewport]} · {d.page_name}</span>
        </div>
        <p className="mt-1 text-slate-600">{d.evidence}</p>
        <p className="mt-0.5 text-slate-900"><span className="text-sky-700 font-medium">Correzione:</span> {d.fix}</p>
      </div>
      <button className="self-start text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 shrink-0" disabled={hook.busy === `dismiss-${d.id}`}
        onClick={() => hook.dismiss(d.id, !d.dismissed)} data-testid={`review-defect-dismiss-${d.id}`} title="Non conta nelle statistiche dei difetti ricorrenti">
        {d.dismissed ? <><RotateCcw size={11} />Ripristina</> : <><EyeOff size={11} />Non è un difetto</>}
      </button>
    </li>
  );
}

export default function DefectsStep({ rev, hook }) {
  const groups = useMemo(() => groupDefects(rev), [rev]);
  const [sel, setSel] = useState({});
  const [unlock, setUnlock] = useState(false);
  useEffect(() => {
    const init = {};
    groups.forEach((g) => g.defects.forEach((d) => { init[d.id] = !d.dismissed && d.severity !== "low" && g.kind === "section"; }));
    setSel(init);
  }, [rev.id, rev.defects_total]); // eslint-disable-line react-hooks/exhaustive-deps
  const targets = groups.map((g) => ({ kind: g.kind, id: g.id, defect_ids: g.defects.filter((d) => sel[d.id] && !d.dismissed).map((d) => d.id) })).filter((t) => t.defect_ids.length);
  const shared = targets.some((t) => t.kind === "component");
  const pages = Object.values(rev.analysis?.pages || {});
  const n = targets.length;
  return (
    <div className="space-y-4" data-testid="review-defects">
      <Scores pages={pages} />
      {!groups.length && <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="review-no-defects">Nessun difetto visivo rilevato nelle pagine analizzate.</p>}
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.id} className="rounded-xl border border-slate-200 bg-white p-3 grid gap-3 md:grid-cols-[220px_1fr]" data-testid={`review-group-${g.id}`}>
            <ReviewImage rid={rev.id} name={cropOf(rev, g)} className="w-full max-h-56 object-cover object-top rounded-md border" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900">{targetTitle(g)}</p>
              <ul className="divide-y divide-slate-100">
                {g.defects.map((d) => <DefectRow key={d.id} d={d} on={!!sel[d.id]} toggle={(v) => setSel((s) => ({ ...s, [d.id]: !!v }))} hook={hook} />)}
              </ul>
            </div>
          </div>
        ))}
      </div>
      {groups.length > 0 && (
        <div className="sm:sticky bottom-0 rounded-xl border border-slate-200 bg-white/95 backdrop-blur p-4 space-y-3 shadow-sm" data-testid="review-fix-box">
          <p className="text-xs text-slate-600">Correzioni raggruppate per pagina: {n} {n === 1 ? "elemento" : "elementi"} scelti. Niente viene applicato finché non approvi il prima/dopo.</p>
          {groups.some((g) => g.kind === "component") && (
            <label className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
              <span>Sblocca elementi condivisi (header/footer: la correzione vale per tutte le pagine)</span>
              <Switch checked={unlock} onCheckedChange={setUnlock} data-testid="review-unlock-shared-switch" />
            </label>
          )}
          <CostButton path={`/visual-reviews/${rev.id}/fix/estimate`} body={n ? { targets, unlock_shared: unlock } : null} disabled={!n || (shared && !unlock)}
            hint={!n ? "Scegli almeno un difetto da correggere" : "Attiva «Sblocca elementi condivisi» per correggere header e footer"}
            label="Prepara correzioni" busy={hook.busy === "fix"} onRun={(est) => hook.fix({ targets, unlock_shared: unlock }, est)} testid="review-fix-btn" />
        </div>
      )}
    </div>
  );
}
